import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef, Optional, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import * as newAddressData from 'assets/json/new-address.json';
import { UserAddressService } from '@app/api/api';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { currentUser, User } from '@app/core/auth';
import { AppState } from '@app/core/reducers';
import { ToastrService } from 'ngx-toastr';
import { UtilService } from '@app/services/util.service';

@Component({
  selector: 'meu-new-address-tab',
  templateUrl: './new-address-tab.component.html',
  styleUrls: ['./new-address-tab.component.scss'],
})
export class NewAddressTabComponent implements OnInit {
  @Input() isModal: boolean = false;
  @Input() addressId: any;
  @Input() targetUserId: any;
  @Output() isCreateDone = new EventEmitter<boolean>();
  @Output() isEditing = new EventEmitter<boolean>();
  isEditMode: boolean = false;

  addressForm = new FormGroup({
    fullname: new FormControl('', [Validators.required]),
    province: new FormControl('', [Validators.required]),
    district: new FormControl(''),
    ward: new FormControl('', [Validators.required]),
    phone: new FormControl('', [Validators.required]),
    address: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    is_company: new FormControl('', [Validators.required]),
    is_default: new FormControl(''),
    user_id: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
	note: new FormControl(''),
  });

  lstAddressJson: any = [];
  lstProvince: any = [];
  lstWard: any = [];
  filteredOptionsLstProvince: Observable<string[]>;
  filteredOptionsLstWard: Observable<string[]>;
  user$: Observable<User>;
  myInfo: any;
  hasFormErrors: boolean = false;
  addressData: any;

  isDialogModal: boolean = false;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private toastr: ToastrService,
    private userAddressService: UserAddressService,
    @Optional() public dialogRef: MatDialogRef<NewAddressTabComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    if (this.data && this.data.isModal) {
      this.isModal = true;
      this.isDialogModal = true;
    }
    if (this.data && this.data.userId) {
      this.targetUserId = this.data.userId;
    }
    this.lstAddressJson = Object.values(JSON.parse(JSON.stringify(newAddressData.default)));
    this.lstProvince = this.lstAddressJson.map((x) => x.Name);
    this.user$ = this.store.pipe(select(currentUser));
    this.user$.subscribe((user) => {
      this.myInfo = user;
    });
    this.filteredOptionsLstProvince = this.addressForm.get('province').valueChanges.pipe(
      startWith(''),
      map((value) => this._filterProvince(value))
    );
    if (this.addressId) {
      this.isEditMode = true;
      this.getAddressByID();
    }
  }

  getAddressByID() {
    this.userAddressService.apiUserAddressesIdGet(this.addressId).subscribe((res) => {
      if (res.success) {
        this.addressData = res.data;
        this.addressForm.patchValue({
          fullname: res.data.fullname,
          province: res.data.province,
          district: res.data.district,
          ward: res.data.ward,
          phone: res.data.phone,
          address: res.data.address,
          is_company: res.data.is_company,
          is_default: res.data.is_default,
          user_id: res.data.user_id,
          country: res.data.country || 'Việt Nam',
		  note: res.data.note
        });
        this.updateLstWard(true);
      }
    });
  }

  private _filterProvince(value: string): string[] {
    if (value != null) {
      const filterValue = UtilService.slugify(value.toLowerCase());
      return this.lstProvince.filter((option) =>
        UtilService.slugify(option.toLowerCase()).includes(filterValue)
      );
    }
  }

  private _filterWard(value: string): string[] {
    if (value != null) {
      const filterValue = UtilService.slugify(value.toLowerCase());
      return this.lstWard
        .map((x) => x.FullName)
        .filter((option) => UtilService.slugify(option.toLowerCase()).includes(filterValue));
    }
  }

  updateLstWard(keepWard: boolean = false) {
    if (this.addressForm.value.province != null && this.addressForm.value.province != '') {
      if (!keepWard) {
        this.addressForm.patchValue({ ward: '' });
      }
      let province = this.lstAddressJson.find((x) => x.Name == this.addressForm.value.province);
      this.lstWard = [];
      if (province != null && province.Wards) {
        this.lstWard = Object.values(JSON.parse(JSON.stringify(province.Wards)));
      }
      this.filteredOptionsLstWard = this.addressForm.get('ward').valueChanges.pipe(
        startWith(''),
        map((value) => this._filterWard(value))
      );
      this.addressForm.patchValue({ district: null});
    }
  }

  createMyNewAddress() {
    this.addressForm.patchValue({
      user_id: this.targetUserId || (this.myInfo ? this.myInfo.id : ''),
      country: 'Việt Nam',
      district: null,
    });
    const controls = this.addressForm.controls;
    if (this.addressForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }
    if (this.addressForm.value.is_default == '' || this.addressForm.value.is_default == null) {
      this.addressForm.patchValue({ is_default: false });
    }
    this.userAddressService.apiUserAddressesPost(this.addressForm.value).subscribe((res) => {
      if (res.success) {
        this.toastr.success('Thêm địa chỉ thành công');
        this.addressForm.reset();
        this.addressForm.patchValue({ province: '', district: null, ward: '' });
        this.lstWard = [];
        this.isCreateDone.emit(true);
        if (!this.isModal) {
          this.router.navigate(['/myprofile/mybookaddress']);
        }
        if (this.dialogRef) {
          this.dialogRef.close({ event: true });
        }
      }
    });
  }

  updateMyAddress() {
    this.addressForm.patchValue({
      district: null,
    });
    const controls = this.addressForm.controls;
    if (this.addressForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }
    this.userAddressService.apiUserAddressesIdPut(this.addressId, this.addressForm.value).subscribe((res) => {
      if (res.success) {
        this.toastr.success('Cập nhật địa chỉ thành công');
        this.router.navigate(['/myprofile/mybookaddress']);
      }
    });
  }

  cancel() {
    this.addressForm.reset();
    this.addressForm.patchValue({ province: '', district: null, ward: '' });
    this.lstWard = [];
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.isEditing.emit(false);
    this.isCreateDone.emit(true);
    if (!this.isModal) {
      this.router.navigate(['/myprofile/mybookaddress']);
    }
  }
}
